import React, { useState, useEffect, useRef } from 'react';
import Card from '../common/Card';
import MiniLoader from '../common/MiniLoader';
import { useContracts } from '../../hooks/useContract';
import { useNotify } from '../../context/NotificationContext';
import { parseError } from '../../utils/errors';

const RouterFlags = () => {
  const { storage } = useContracts();
  const notify = useNotify();

  const [walletEnabled, setWalletEnabled] = useState(true);
  const [poolEnabled, setPoolEnabled] = useState(true);
  const [loading, setLoading] = useState(false);
  const [busy, setBusy] = useState(null);
  const runIdRef = useRef(0);

  const load = async () => {
    const myRun = ++runIdRef.current;
    setLoading(true);
    try {
      const [w, p] = await Promise.all([
        storage.routerWalletEnabled(),
        storage.routerPoolEnabled()
      ]);
      if (myRun !== runIdRef.current) return;
      setWalletEnabled(w);
      setPoolEnabled(p);
    } catch (_) { /* ignore */ }
    finally {
      if (myRun === runIdRef.current) setLoading(false);
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line
  }, []);

  const toggleWallet = async () => {
    if (busy) return;
    setBusy('wallet');
    const loadingId = notify.loading(`${walletEnabled ? 'Disabling' : 'Enabling'} router wallet...`);
    try {
      const tx = await storage.setRouterWalletEnabled(!walletEnabled);
      notify.dismiss(loadingId);
      const mining = notify.loading('Confirming...');
      await tx.wait();
      notify.dismiss(mining);
      notify.success(`Router wallet ${!walletEnabled ? 'enabled' : 'disabled'}`);
      setWalletEnabled(!walletEnabled);
    } catch (err) {
      notify.dismiss(loadingId);
      notify.error(parseError(err));
    } finally {
      setBusy(null);
    }
  };

  const togglePool = async () => {
    if (busy) return;
    setBusy('pool');
    const loadingId = notify.loading(`${poolEnabled ? 'Disabling' : 'Enabling'} router pool...`);
    try {
      const tx = await storage.setRouterPoolEnabled(!poolEnabled);
      notify.dismiss(loadingId);
      const mining = notify.loading('Confirming...');
      await tx.wait();
      notify.dismiss(mining);
      notify.success(`Router pool ${!poolEnabled ? 'enabled' : 'disabled'}`);
      setPoolEnabled(!poolEnabled);
    } catch (err) {
      notify.dismiss(loadingId);
      notify.error(parseError(err));
    } finally {
      setBusy(null);
    }
  };

  return (
    <Card title="Router Flags" subtitle="Distribution routing toggles" glow>
      {loading ? (
        <div className="text-center" style={{ padding: 20 }}><MiniLoader /></div>
      ) : (
        <div className="flex flex-col gap-sm">
          <div className="admin-toggle">
            <div className="flex-1">
              <div style={{ fontWeight: 600, fontSize: 14 }}>Router Wallet (1.8%)</div>
              <div className="text-muted" style={{ fontSize: 12 }}>
                {walletEnabled ? 'Sending to router wallet' : 'Redirected to members pool'}
              </div>
            </div>
            <div
              className={`toggle-switch ${walletEnabled ? 'on' : ''} ${busy === 'wallet' ? 'disabled' : ''}`}
              onClick={toggleWallet}
              role="button"
              aria-label="Toggle router wallet"
            />
          </div>

          <div className="admin-toggle">
            <div className="flex-1">
              <div style={{ fontWeight: 600, fontSize: 14 }}>Router Pool (1.2%)</div>
              <div className="text-muted" style={{ fontSize: 12 }}>
                {poolEnabled ? 'Accumulating in router pool' : 'Redirected to members / missing wallet'}
              </div>
            </div>
            <div
              className={`toggle-switch ${poolEnabled ? 'on' : ''} ${busy === 'pool' ? 'disabled' : ''}`}
              onClick={togglePool}
              role="button"
              aria-label="Toggle router pool"
            />
          </div>
        </div>
      )}
    </Card>
  );
};

export default RouterFlags;
